import { X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import File from '@/components/ui/file';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { useQualityDetailContext } from '@/contexts/QualityContext';
import { DETAIL_MODE, DETAIL_TYPE, useDetailMode } from '@/hooks/useDetailMode';

import { YES_NO_OPTIONS } from './QualityPlanView';

const IMPACT_LIST = [
  {
    description:
      '1. Does the quality event affects product/service offering specification',
    key: 'nonconformitySpecification',
  },
  {
    description:
      '2. Does the quality event affect the organisation’s compliance to the current QMS.',
    key: 'nonconformityQms',
  },
  {
    description:
      '3. Does the quality event affects the organisation’s compliance to the required standards and/or regulations.',
    key: 'nonconformityRegulations',
  },
  {
    description:
      '4. Does the quality event affect the organisation’s commitment to health and safety?',
    key: 'nonconformitySafety',
  },
];

const QualityNonconformityView = () => {
  const { mode } = useDetailMode(DETAIL_TYPE.QUALITY);
  const { form, setFormByKey } = useQualityDetailContext();
  const hasNonconformity = IMPACT_LIST.some(
    ({ key }) => String(form[key]?.check) === '1'
  );
  const details = form.nonconformityDetails || {};
  const statement = details.statement || {};
  const rootCause = details.rootCause || {};

  const updateDetails = (section, values) => {
    setFormByKey('nonconformityDetails', {
      ...details,
      [section]: { ...details[section], ...values },
    });
  };

  const removeAttachment = (key) => {
    updateDetails('statement', {
      attachments: (statement.attachments || []).filter(
        (attachment) => attachment.key !== key
      ),
    });
  };

  return (
    <div className="space-y-4">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Risk Impact Assessment</TableHead>
              <TableHead className="w-40">Yes / No</TableHead>
              <TableHead className="">Additional Remarks (if any)</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {IMPACT_LIST.map(({ description, key }) => (
              <TableRow key={key}>
                <TableCell>{description}</TableCell>
                <TableCell>
                  {mode === DETAIL_MODE.STATIC ? (
                    form[key]?.check === '1' ? (
                      'Yes'
                    ) : (
                      'No'
                    )
                  ) : (
                    <Select
                      onChange={({ target }) =>
                        setFormByKey(key, { ...form[key], check: target.value })
                      }
                      value={form[key]?.check}
                    >
                      {!form[key]?.check && <option value="">-</option>}
                      {YES_NO_OPTIONS.map(({ label, value }) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </Select>
                  )}
                </TableCell>
                <TableCell>
                  {mode === DETAIL_MODE.STATIC ? (
                    form[key]?.remark || '-'
                  ) : (
                    <Input
                      className="pl-1"
                      onChange={({ target }) =>
                        setFormByKey(key, {
                          ...form[key],
                          remark: target.value,
                        })
                      }
                      value={form[key]?.remark || ''}
                    />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {hasNonconformity && (
        <div className="space-y-4">
          <p className="italic">
            The quality event is identified as a nonconformity. Please provide
            the nonconformity statement and identify the root cause.
          </p>

          <section className="rounded-md border p-4 space-y-3">
            <h3 className="font-medium">
              Nonconformity Statement <span className="text-destructive">*</span>
            </h3>
            <p className="text-sm text-muted-foreground">
              State the reference or requirement that was not met and describe
              the objective evidence.
            </p>
            {mode === DETAIL_MODE.STATIC ? (
              <p className="whitespace-pre-wrap">{statement.text || '-'}</p>
            ) : (
              <Textarea
                onChange={({ target }) =>
                  updateDetails('statement', { text: target.value })
                }
                rows={6}
                value={statement.text || ''}
              />
            )}

            {!!statement.attachments?.length && (
              <div className="space-y-2">
                {statement.attachments.map((attachment) => (
                  <div
                    className="flex items-center justify-between rounded border px-3 py-2 text-sm"
                    key={attachment.key}
                  >
                    <span>{attachment.originalName}</span>
                    {mode !== DETAIL_MODE.STATIC && (
                      <Button
                        aria-label={`Remove ${attachment.originalName}`}
                        onClick={() => removeAttachment(attachment.key)}
                        size="icon"
                        type="button"
                        variant="ghost"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {mode !== DETAIL_MODE.STATIC && (
              <File
                ignoreBase64
                multiple
                onChange={({ files }) =>
                  updateDetails('statement', {
                    pendingAttachments: Array.from(files || []),
                  })
                }
                placeholder="Upload Attachments"
              />
            )}
          </section>

          <section className="rounded-md border p-4 space-y-3">
            <h3 className="font-medium">
              Identify the Root Cause <span className="text-destructive">*</span>
            </h3>
            <p className="text-sm text-muted-foreground">
              Describe the potential causes and conclusion of the root cause.
            </p>
            {mode === DETAIL_MODE.STATIC ? (
              <p className="whitespace-pre-wrap">{rootCause.text || '-'}</p>
            ) : (
              <Textarea
                onChange={({ target }) =>
                  updateDetails('rootCause', { text: target.value })
                }
                rows={6}
                value={rootCause.text || ''}
              />
            )}
          </section>
        </div>
      )}
    </div>
  );
};

export default QualityNonconformityView;
